import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import type {
  CustomerVendorSubscription,
  ReconciliationVariance,
  RenewalScheduleItem,
  VendorImportRenewalRow,
} from '@techgitter/contracts';
import { IconButton, Tooltip } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { formatCadenceLabel, formatStatusLabel } from './reconciliationLabels';
import { getVendorSubscriptions } from '../../services/vendorSubscriptionService';
import { fetchVariances, resolveVariance, fetchRenewalRows } from '../../services/reconciliationApi';
import { getVendors } from '../../services/vendorService';
import { useRenewalSchedule } from '../../hooks/useRenewalSchedule';
import { renewalRowMatchesCustomer } from './reconciliationFilter';
import { SubscriptionEditDialog } from './SubscriptionEditDialog';
import { ScheduleLineEditDialog } from './ScheduleLineEditDialog';
import { ResolveDialog, ResolveRequest } from './ResolveDialog';
import { TERM_MISMATCH_TOOLTIP } from './termMismatch';
import { useRerunReconcile } from './useRerunReconcile';

/** A coverage row to render: either a single variance or a collapsed term-mismatch pair. */
type CoverageRow =
  | { kind: 'single'; v: ReconciliationVariance }
  | { kind: 'termMismatch'; committed: ReconciliationVariance; billed: ReconciliationVariance };

/** Collapse complementary term-mismatch coverage variances (committed on one term,
 *  billed on another) into one row; everything else stays as-is. */
function buildCoverageRows(coverage: ReconciliationVariance[]): CoverageRow[] {
  const byId = new Map(coverage.map((v) => [v.id, v]));
  const consumed = new Set<string>();
  const rows: CoverageRow[] = [];
  for (const v of coverage) {
    if (consumed.has(v.id)) continue;
    const counterpart =
      v.termMismatch && v.termMismatchCounterpartId
        ? byId.get(v.termMismatchCounterpartId)
        : undefined;
    if (counterpart && !consumed.has(counterpart.id)) {
      const committed = Number(v.expectedValue) > 0 ? v : counterpart;
      const billed = committed === v ? counterpart : v;
      rows.push({ kind: 'termMismatch', committed, billed });
      consumed.add(v.id);
      consumed.add(counterpart.id);
    } else {
      rows.push({ kind: 'single', v });
    }
  }
  return rows;
}

const headCellSx = {
  backgroundColor: 'rgba(0, 0, 0, 0.08)',
  color: 'rgba(0,0,0,0.8)',
  fontSize: '0.72rem',
  fontWeight: 'bold',
  textTransform: 'uppercase' as const,
};
const bodyCellSx = { fontSize: '0.8rem' };

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

const isAnnual = (term?: string | null) => /annual/i.test(term ?? '');

/** True when the scheduled renewal matches the current state — renewing as-is
 *  with no change (plan, seats and term all unchanged). Null scheduled fields
 *  mean "unchanged". Cancelling rows are handled separately. */
function renewsUnchanged(r: VendorImportRenewalRow): boolean {
  const planSame = r.scheduledPlan == null || r.scheduledPlan === r.sku;
  const seatsSame = r.scheduledSeats == null || r.scheduledSeats === r.currentSeats;
  const termSame =
    r.scheduledBillingTerm == null || r.scheduledBillingTerm === r.currentBillingTerm;
  return planSame && seatsSame && termSame;
}

/** Add one calendar day to a "YYYY-MM-DD" date, returning the same format.
 *  Parsed as local midnight so the day arithmetic is timezone-stable. */
function plusOneDayIso(iso?: string | null): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Prefill for the guided term-mismatch resolution. The action date is the
 * **annual** invoice-schedule line's next run + 1 day — the day after we next
 * send the annual invoice, when the vendor subscription is switched to renew
 * annually (co-termed with billing). Falls back to undefined (operator picks)
 * if no annual schedule line is visible.
 */
function termMismatchPrefill(
  committed: ReconciliationVariance,
  billed: ReconciliationVariance,
  scheduleItems: RenewalScheduleItem[],
): { resolutionNotes: string; actionDate?: string } {
  const annualVar = isAnnual(committed.vendorProductBillingTerm) ? committed : billed;
  const otherVar = annualVar === committed ? billed : committed;
  const annualTerm = annualVar.vendorProductBillingTerm ?? 'annual';
  const otherTerm = otherVar.vendorProductBillingTerm ?? 'monthly';

  // The annual invoice-schedule lines drive the annual coverage. Prefer one
  // whose SKU matches a side of the mismatch; otherwise the earliest annual run.
  const skus = [committed.vendorProductSku, billed.vendorProductSku]
    .filter(Boolean)
    .map((s) => s!.toLowerCase());
  const annualLines = scheduleItems.filter((it) => isAnnual(it.frequency) && !it.paused);
  const matched = annualLines.filter((it) => skus.includes((it.productSku ?? '').toLowerCase()));
  const pool = matched.length ? matched : annualLines;
  const earliest = pool.reduce<RenewalScheduleItem | undefined>(
    (a, b) => (a && a.nextRun <= b.nextRun ? a : b),
    undefined,
  );

  return {
    resolutionNotes: `Set ${otherTerm} license to renew as ${annualTerm}`,
    actionDate: plusOneDayIso(earliest?.nextRun),
  };
}

interface Props {
  open: boolean;
  customerId: string;
  customerName?: string | null;
  /** Admin — allows editing the commitment (subscriptions) inline. */
  canEdit?: boolean;
  onClose: () => void;
}

/**
 * Per-customer reconciliation drill-down: the subscriptions we hold (supplier
 * truth / commitment), the recurring invoice schedule (billing — each line's
 * next run + frequency, SKU term visible), and the coverage variances comparing
 * the two (expected = commitment, billed = invoice). Read-only — resolution
 * happens from the Variances tab. Import-history is a deferred follow-up (needs
 * alias resolution).
 */
export const CustomerDrillDownDialog: React.FC<Props> = ({
  open,
  customerId,
  customerName,
  canEdit = false,
  onClose,
}) => {
  const [subscriptions, setSubscriptions] = useState<CustomerVendorSubscription[]>([]);
  // Cancelled commitments are history — the engine ignores them (billing_renewal
  // skips SUB_CANCELLED) — so they are hidden by default and revealed on demand.
  const [showCancelled, setShowCancelled] = useState(false);
  const [coverage, setCoverage] = useState<ReconciliationVariance[]>([]);
  const [renewalRows, setRenewalRows] = useState<VendorImportRenewalRow[]>([]);
  const [resolutions, setResolutions] = useState<ReconciliationVariance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editSub, setEditSub] = useState<CustomerVendorSubscription | null>(null);
  const [editLine, setEditLine] = useState<RenewalScheduleItem | null>(null);
  // The term-mismatch pair currently being resolved (opens a prefilled ResolveDialog).
  const [resolveTarget, setResolveTarget] = useState<{
    committed: ReconciliationVariance;
    billed: ReconciliationVariance;
  } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Re-run reconcile (Control 1 + Control 2) over the existing latest batch — no
  // Synnex re-pull. Runs the full batch (reliable), then refreshes this dialog so
  // breaks whose data was fixed clear.
  const { rerun, running: rerunning } = useRerunReconcile();
  const handleRerun = async () => {
    try {
      const res = await rerun();
      window.setTimeout(() => setReloadKey((k) => k + 1), res.running ? 30_000 : 0);
    } catch {
      /* surfaced via the button's disabled state resetting; drill-down has no toast */
    }
  };

  const coverageRows = useMemo(() => buildCoverageRows(coverage), [coverage]);

  // Subscriptions held, minus cancelled ones unless the toggle is on. The full
  // `subscriptions` list still drives the renewal-row ref matching below.
  const cancelledCount = useMemo(
    () => subscriptions.filter((s) => s.status === 'cancelled').length,
    [subscriptions],
  );
  const visibleSubscriptions = useMemo(
    () => (showCancelled ? subscriptions : subscriptions.filter((s) => s.status !== 'cancelled')),
    [subscriptions, showCancelled],
  );

  // Renewals soonest-expiry first (the actionable order).
  const renewalRowsSorted = useMemo(() => {
    const ts = (v?: string | null) => {
      if (!v) return Infinity;
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? Infinity : t;
    };
    return [...renewalRows].sort((a, b) => ts(a.currentEndDate) - ts(b.currentEndDate));
  }, [renewalRows]);

  // Billing side: the customer's recurring invoice schedule line items, each
  // carrying its schedule's next run + frequency. The SKU (e.g.
  // "BML.M365BS.Monthly") exposes the billed term — so a term mismatch against
  // the commitment above is visible without leaving the dialog.
  const {
    schedule: scheduleItems,
    isLoading: scheduleLoading,
    retry: refreshSchedule,
  } = useRenewalSchedule(customerId);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [subs, cov, resolved] = await Promise.all([
          getVendorSubscriptions({ customerId }, controller.signal),
          fetchVariances({ type: 'coverage', status: 'open', customerId }, controller.signal),
          fetchVariances({ status: 'resolved', customerId }, controller.signal),
        ]);
        if (controller.signal.aborted) return;
        setSubscriptions(subs);
        setCoverage(cov);
        setResolutions(resolved);

        // ALL renewals for this customer (rows, not just variances). Renewal
        // rows carry no customer GUID, so pull each subscription-supplier's
        // latest renewal rows and match by the customer's subscription refs
        // (the GUID Synnex embeds in the SKU) — robust to Synnex labelling the
        // account under a different name than our customer — falling back to a
        // customer-name substring match. `subs` (this customer's subscriptions)
        // provides the ref set.
        const suppliers = (await getVendors(controller.signal)).filter(
          (v) => v.isSubscriptionSupplier,
        );
        const perVendor = await Promise.all(
          suppliers.map((v) => fetchRenewalRows({ vendorId: v.vendorId }, controller.signal)),
        );
        if (controller.signal.aborted) return;
        const subRefs = new Set(
          subs
            .map((s) => s.subscriptionRef?.trim().toLowerCase())
            .filter((r): r is string => !!r),
        );
        const mine = perVendor
          .flat()
          .filter((r) => renewalRowMatchesCustomer(r, customerName, subRefs));
        setRenewalRows(mine);
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load customer detail');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [customerId, customerName, reloadKey]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
      >
        <span>{customerName ?? 'Customer'} — Reconciliation Detail</span>
        {canEdit && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleRerun}
            disabled={rerunning}
            title="Re-run reconcile over the existing Synnex batch (Control 1 + coverage) — no re-pull"
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            {rerunning ? 'Reconciling…' : 'Re-run reconcile'}
          </Button>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Subscriptions held{' '}
                  <Chip size="small" label={visibleSubscriptions.length} sx={{ ml: 0.5 }} />
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showCancelled}
                      onChange={(e) => setShowCancelled(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      {cancelledCount > 0
                        ? `Show cancelled (${cancelledCount})`
                        : 'Show cancelled'}
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
              </Stack>
              {visibleSubscriptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {cancelledCount > 0
                    ? `No active subscriptions — ${cancelledCount} cancelled hidden.`
                    : 'No subscriptions recorded for this customer.'}
                </Typography>
              ) : (
                <TableContainer component={Box}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Vendor product</TableCell>
                        <TableCell sx={headCellSx}>Vendor</TableCell>
                        <TableCell sx={headCellSx} align="right">Seats</TableCell>
                        <TableCell sx={headCellSx}>Cadence</TableCell>
                        <TableCell sx={headCellSx}>Renewal</TableCell>
                        <TableCell sx={headCellSx}>Status</TableCell>
                        {canEdit && <TableCell sx={headCellSx} align="right">Edit</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleSubscriptions.map((s, i) => (
                        <TableRow
                          key={s.id}
                          sx={{ backgroundColor: i % 2 === 1 ? 'action.hover' : 'transparent' }}
                        >
                          <TableCell sx={bodyCellSx}>
                            {s.vendorProductName ?? '—'}
                            {s.label && (
                              <Box
                                component="span"
                                sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}
                              >
                                {s.label}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{s.vendorName ?? '—'}</TableCell>
                          <TableCell sx={bodyCellSx} align="right">{s.seats}</TableCell>
                          <TableCell sx={bodyCellSx}>{formatCadenceLabel(s.billingCadence)}</TableCell>
                          <TableCell sx={bodyCellSx}>{fmtDate(s.renewalDate)}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {formatStatusLabel(s.status)}
                            </Box>
                          </TableCell>
                          {canEdit && (
                            <TableCell sx={bodyCellSx} align="right">
                              <Tooltip title="Edit commitment (seats / term / status)">
                                <IconButton size="small" onClick={() => setEditSub(s)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Invoice schedule (billing){' '}
                <Chip size="small" label={scheduleItems.length} sx={{ ml: 0.5 }} />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                What the recurring invoice covers, with each schedule's next run + frequency.
                The SKU's term should match the committed cadence above.
              </Typography>
              {scheduleLoading ? (
                <Stack alignItems="center" sx={{ py: 3 }}>
                  <CircularProgress size={20} />
                </Stack>
              ) : scheduleItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No invoice schedule items for this customer.
                </Typography>
              ) : (
                <TableContainer component={Box} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Product</TableCell>
                        <TableCell sx={headCellSx} align="right">Qty</TableCell>
                        <TableCell sx={headCellSx}>Frequency</TableCell>
                        <TableCell sx={headCellSx}>Next run</TableCell>
                        <TableCell sx={headCellSx}>Status</TableCell>
                        {canEdit && <TableCell sx={headCellSx} align="right">Edit</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scheduleItems.map((it, i) => (
                        <TableRow
                          key={`${it.scheduleId}-${it.productSku}-${i}`}
                          sx={{ backgroundColor: i % 2 === 1 ? 'action.hover' : 'transparent' }}
                        >
                          <TableCell sx={bodyCellSx}>
                            <Stack>
                              <span>{it.productName || it.productSku}</span>
                              {it.productSku && it.productSku !== it.productName && (
                                <Typography variant="caption" color="text.secondary">
                                  {it.productSku}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">{it.quantity}</TableCell>
                          <TableCell sx={bodyCellSx}>{it.frequency || '—'}</TableCell>
                          <TableCell sx={bodyCellSx}>{fmtDate(it.nextRun)}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {formatStatusLabel(it.paused ? 'paused' : 'active')}
                            </Box>
                          </TableCell>
                          {canEdit && (
                            <TableCell sx={bodyCellSx} align="right">
                              <Tooltip title="Edit billed quantity (RS + SM4RT)">
                                <IconButton size="small" onClick={() => setEditLine(it)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Coverage variances{' '}
                <Chip size="small" label={coverageRows.length} sx={{ ml: 0.5 }} />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Expected = committed seats; Billed = what the invoice schedule covers.
              </Typography>
              {coverageRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No coverage variances — billing matches the commitment.
                </Typography>
              ) : (
                <TableContainer component={Box} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Vendor product</TableCell>
                        <TableCell sx={headCellSx} align="right">Expected</TableCell>
                        <TableCell sx={headCellSx} align="right">Billed</TableCell>
                        <TableCell sx={headCellSx}>Status</TableCell>
                        {canEdit && <TableCell sx={headCellSx} align="right">Action</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coverageRows.map((row, i) => {
                        const bg = i % 2 === 1 ? 'action.hover' : 'transparent';
                        if (row.kind === 'termMismatch') {
                          const { committed, billed } = row;
                          return (
                            <TableRow key={`${committed.id}-${billed.id}`} sx={{ backgroundColor: bg }}>
                              <TableCell sx={bodyCellSx}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <span>{committed.vendorProductName ?? committed.vendorProductSku ?? '—'}</span>
                                  <Tooltip title={TERM_MISMATCH_TOOLTIP}>
                                    <Chip size="small" color="warning" label="Term mismatch" />
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                              <TableCell sx={bodyCellSx} align="right">
                                {committed.expectedValue ?? '—'} × {committed.vendorProductBillingTerm ?? '—'}
                              </TableCell>
                              <TableCell sx={bodyCellSx} align="right">
                                {billed.actualValue ?? '—'} × {billed.vendorProductBillingTerm ?? '—'}
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                  {formatStatusLabel('open')}
                                </Box>
                              </TableCell>
                              {canEdit && (
                                <TableCell sx={bodyCellSx} align="right">
                                  <Tooltip title="Resolve as Adjust Vendor Subscription, deferred to the day after the next annual invoice">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => setResolveTarget({ committed, billed })}
                                    >
                                      Create Resolution Action
                                    </Button>
                                  </Tooltip>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        }
                        const v = row.v;
                        return (
                          <TableRow key={v.id} sx={{ backgroundColor: bg }}>
                            <TableCell sx={bodyCellSx}>
                              {v.vendorProductName ?? v.vendorProductSku ?? '—'}
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="right">{v.expectedValue ?? '—'}</TableCell>
                            <TableCell sx={bodyCellSx} align="right">{v.actualValue ?? '—'}</TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Box component="span" sx={{ fontWeight: 600 }}>
                                {formatStatusLabel(v.status)}
                              </Box>
                            </TableCell>
                            {canEdit && <TableCell sx={bodyCellSx} />}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Renewals{' '}
                <Chip size="small" label={renewalRows.length} sx={{ ml: 0.5 }} />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Upcoming supplier renewals for this customer (Control 3): current → what it renews
                into, with the term's expiry and the scheduled renewal date. Where the scheduled
                state differs from the commitment, a renewal variance is raised.
              </Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                <Typography component="li" variant="caption" color="text.secondary">
                  New monthly renewals at Synnex renew to the end of the current month.
                </Typography>
                <Typography component="li" variant="caption" color="text.secondary">
                  New annual renewals at Synnex renew to the end of the previous month.
                </Typography>
              </Box>
              {renewalRowsSorted.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No upcoming renewals for this customer.
                </Typography>
              ) : (
                <TableContainer component={Box} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Current</TableCell>
                        <TableCell sx={headCellSx}>Renews into</TableCell>
                        <TableCell sx={headCellSx}>Expires</TableCell>
                        <TableCell sx={headCellSx}>Renews</TableCell>
                        <TableCell sx={headCellSx}>Flags</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {renewalRowsSorted.map((r, i) => (
                        <TableRow
                          key={r.id}
                          sx={{ backgroundColor: i % 2 === 1 ? 'action.hover' : 'transparent' }}
                        >
                          <TableCell sx={bodyCellSx}>
                            <Stack>
                              <span>{r.sku ?? '—'}</span>
                              <Typography variant="caption" color="text.secondary">
                                {r.currentSeats ?? '—'} seats{r.currentBillingTerm ? ` · ${r.currentBillingTerm}` : ''}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {r.cancelling ? (
                              // Supplier reports the sub is expiring — call it out.
                              <span style={{ fontWeight: 600 }}>Cancels</span>
                            ) : renewsUnchanged(r) ? (
                              // Renewing as-is (no change) — leave blank.
                              null
                            ) : (
                              // A change at renewal — show what it renews into.
                              <Stack>
                                <span>{r.scheduledPlan ?? '—'}</span>
                                <Typography variant="caption" color="text.secondary">
                                  {r.scheduledSeats ?? '—'} seats{r.scheduledBillingTerm ? ` · ${r.scheduledBillingTerm}` : ''}
                                </Typography>
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{fmtDate(r.currentEndDate)}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            {fmtDate(r.scheduledEndDate)}
                            {typeof r.daysRemaining === 'number' ? ` · ${r.daysRemaining}d` : ''}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Stack direction="row" spacing={0.5}>
                              {r.imminent && (
                                <Tooltip title="Renews within the imminence window — act soon">
                                  <Chip size="small" color="warning" label="imminent" />
                                </Tooltip>
                              )}
                              {r.isTrial && <Chip size="small" label="trial" />}
                              {r.trialAutoRenews && <Chip size="small" color="warning" label="converting" />}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Previous resolutions{' '}
                <Chip size="small" label={resolutions.length} sx={{ ml: 0.5 }} />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Resolved breaks for this customer. A future Action Date means the break is
                deferred (suppressed) until then.
              </Typography>
              {resolutions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No resolutions recorded for this customer.
                </Typography>
              ) : (
                <TableContainer component={Box} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Type</TableCell>
                        <TableCell sx={headCellSx}>Vendor product</TableCell>
                        <TableCell sx={headCellSx}>Resolution</TableCell>
                        <TableCell sx={headCellSx}>Action date</TableCell>
                        <TableCell sx={headCellSx}>Resolved</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resolutions.map((v, i) => (
                        <TableRow
                          key={v.id}
                          sx={{ backgroundColor: i % 2 === 1 ? 'action.hover' : 'transparent' }}
                        >
                          <TableCell sx={bodyCellSx}>{v.varianceType}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            {v.vendorProductName ?? v.vendorProductSku ?? '—'}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Stack>
                              <span>{v.resolutionType ?? 'resolved'}</span>
                              {v.resolutionNotes && (
                                <Typography variant="caption" color="text.secondary">
                                  {v.resolutionNotes}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {v.actionDate ? (
                              <Chip size="small" color="info" label={fmtDate(v.actionDate)} />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Stack>
                              <span>{fmtDate(v.resolvedAt)}</span>
                              {v.resolvedBy && (
                                <Typography variant="caption" color="text.secondary">
                                  {v.resolvedBy}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {editSub && (
        <SubscriptionEditDialog
          open
          subscription={editSub}
          onClose={() => setEditSub(null)}
          onSaved={() => {
            setEditSub(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      {editLine && (
        <ScheduleLineEditDialog
          open
          item={editLine}
          onClose={() => setEditLine(null)}
          onSaved={() => {
            setEditLine(null);
            refreshSchedule();          // billing side (renewal-schedule hook cache)
            setReloadKey((k) => k + 1); // coverage may shift once billing changes
          }}
        />
      )}

      {resolveTarget &&
        (() => {
          const { committed, billed } = resolveTarget;
          const prefill = termMismatchPrefill(committed, billed, scheduleItems);
          // Resolve BOTH halves of the pair with the same action so neither lingers
          // open; the Action Date defers (suppresses) each break until then (§3p).
          const resolveBoth = async (req: ResolveRequest) => {
            await resolveVariance(committed.id, req);
            await resolveVariance(billed.id, req);
          };
          return (
            <ResolveDialog
              open
              title="Create resolution action — term mismatch"
              subtitle={`${committed.vendorProductName ?? committed.vendorProductSku ?? 'Vendor product'} — resolves both the committed and billed coverage variances.`}
              showActionDate
              initialResolutionType="adjustVendorSubscription"
              initialNotes={prefill.resolutionNotes}
              initialActionDate={prefill.actionDate}
              onResolve={resolveBoth}
              onClose={() => setResolveTarget(null)}
              onResolved={() => {
                setResolveTarget(null);
                setReloadKey((k) => k + 1);
              }}
            />
          );
        })()}
    </Dialog>
  );
};
